import { createFileRoute, redirect } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'

interface IdeaForAggregate {
  id: string
  author?: { full_name?: string | null } | null
  target_department_name?: string | null
  created_at: string
}

interface StatusBucket {
  code: string
  name: string
  color: string
  count: number
}
interface MyStats {
  total_ideas: number
  recent_30d: number
  by_status: StatusBucket[]
}
interface TopAuthor {
  user_id: string
  full_name: string
  count: number
}
interface ManagerStats {
  total_in_scope: number
  by_status: StatusBucket[]
  top_authors: TopAuthor[]
  avg_ai_score: number | null
}
interface AdminStats {
  total_users: number
  total_active_users: number
  total_ideas: number
  by_status: StatusBucket[]
  ai_cost_month_usd: number
  pipeline_failures_24h: number
}

export const Route = createFileRoute('/dashboard')({
  beforeLoad: async () => {
    const s = useAuth.getState()
    if (s.loading) await s.load()
    if (!useAuth.getState().user) throw redirect({ to: '/login' })
  },
  component: DashboardPage,
})

function StatusBar({ bucket, max }: { bucket: StatusBucket; max: number }) {
  const pct = max > 0 ? (bucket.count / max) * 100 : 0
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-40 truncate text-zinc-700 dark:text-zinc-300">{bucket.name}</span>
      <div className="flex-1 h-4 bg-zinc-100 dark:bg-zinc-700 rounded overflow-hidden">
        <div
          className="h-full rounded"
          style={{ width: `${pct}%`, backgroundColor: bucket.color }}
        />
      </div>
      <span className="w-10 text-right text-zinc-600 dark:text-zinc-400 text-xs font-mono">
        {bucket.count}
      </span>
    </div>
  )
}

function MetricCard({
  label,
  value,
  sub,
}: {
  label: string
  value: string | number
  sub?: string
}) {
  return (
    <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-5">
      <p className="text-xs text-zinc-500 dark:text-zinc-400">{label}</p>
      <p className="mt-1 text-2xl font-bold text-zinc-900 dark:text-zinc-100">{value}</p>
      {sub && <p className="mt-1 text-xs text-zinc-500">{sub}</p>}
    </div>
  )
}

function DashboardPage() {
  const user = useAuth((s) => s.user)
  if (!user) return null

  if (user.role === 'admin') return <AdminDashboardView />
  if (user.role === 'manager') return <ManagerDashboardView />
  return <UserDashboardView />
}

function UserDashboardView() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'me'],
    queryFn: () => api<MyStats>('/api/dashboards/me'),
  })
  if (isLoading || !data)
    return <p className="p-8 text-sm text-zinc-500">Загрузка…</p>
  const max = Math.max(1, ...data.by_status.map((b) => b.count))
  return (
    <div className="max-w-4xl mx-auto p-8 space-y-6">
      <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Мой дашборд</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MetricCard label="Всего идей подано" value={data.total_ideas} />
        <MetricCard label="За последние 30 дней" value={data.recent_30d} />
      </div>
      <section className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-5">
        <h2 className="text-sm font-semibold mb-3 text-zinc-900 dark:text-zinc-100">
          По статусам
        </h2>
        <div className="space-y-2">
          {data.by_status.map((b) => (
            <StatusBar key={b.code} bucket={b} max={max} />
          ))}
        </div>
      </section>
    </div>
  )
}

function ManagerDashboardView() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'manager'],
    queryFn: () => api<ManagerStats>('/api/dashboards/manager'),
  })
  if (isLoading || !data)
    return <p className="p-8 text-sm text-zinc-500">Загрузка…</p>
  const max = Math.max(1, ...data.by_status.map((b) => b.count))
  return (
    <div className="max-w-5xl mx-auto p-8 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
          Дашборд менеджера
        </h1>
        <a
          href="/api/exports/nordgold-report.xlsx"
          className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-sm"
        >
          📥 Excel
        </a>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MetricCard label="Идей в моём scope" value={data.total_in_scope} />
        <MetricCard
          label="Средний AI Score"
          value={data.avg_ai_score !== null ? data.avg_ai_score.toFixed(1) : '—'}
        />
      </div>
      <section className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-5">
        <h2 className="text-sm font-semibold mb-3 text-zinc-900 dark:text-zinc-100">
          Воронка статусов
        </h2>
        <div className="space-y-2">
          {data.by_status.map((b) => (
            <StatusBar key={b.code} bucket={b} max={max} />
          ))}
        </div>
      </section>
      <section className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-5">
        <h2 className="text-sm font-semibold mb-3 text-zinc-900 dark:text-zinc-100">
          Топ авторов
        </h2>
        <ol className="space-y-2 list-decimal list-inside text-sm">
          {data.top_authors.map((a) => (
            <li key={a.user_id} className="flex justify-between">
              <span className="text-zinc-700 dark:text-zinc-300">{a.full_name}</span>
              <span className="text-zinc-500 font-mono">{a.count}</span>
            </li>
          ))}
          {data.top_authors.length === 0 && (
            <li className="text-zinc-500">нет данных</li>
          )}
        </ol>
      </section>
    </div>
  )
}

function AdminDashboardView() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'admin'],
    queryFn: () => api<AdminStats>('/api/dashboards/admin'),
  })
  const { data: ideas } = useQuery({
    queryKey: ['dashboard', 'admin', 'ideas-sample'],
    queryFn: () => api<IdeaForAggregate[]>('/api/ideas?limit=200'),
  })

  const aggregates = useMemo(() => computeAggregates(ideas ?? []), [ideas])

  if (isLoading || !data)
    return <p className="p-8 text-sm text-zinc-500">Загрузка…</p>
  const max = Math.max(1, ...data.by_status.map((b) => b.count))
  return (
    <div className="max-w-6xl mx-auto p-8 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
          Глобальный дашборд
        </h1>
        <a
          href="/api/exports/nordgold-report.xlsx"
          className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-sm"
        >
          📥 Excel
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="Идей всего" value={data.total_ideas} />
        <MetricCard
          label="Пользователей"
          value={data.total_active_users}
          sub={`из ${data.total_users} зарегистрировано`}
        />
        <MetricCard
          label="AI cost / месяц"
          value={`$${data.ai_cost_month_usd.toFixed(2)}`}
        />
        <MetricCard
          label="AI ошибок / 24ч"
          value={data.pipeline_failures_24h}
          sub={
            data.pipeline_failures_24h === 0
              ? 'все pipeline зелёные'
              : 'требует внимания'
          }
        />
      </div>
      <section className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-5">
        <h2 className="text-sm font-semibold mb-3 text-zinc-900 dark:text-zinc-100">
          По статусам
        </h2>
        <div className="space-y-2">
          {data.by_status.map((b) => (
            <StatusBar key={b.code} bucket={b} max={max} />
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <section className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-5">
          <h2 className="text-sm font-semibold mb-3 text-zinc-900 dark:text-zinc-100">
            По подразделениям (топ 10)
          </h2>
          {aggregates.byDept.length === 0 ? (
            <p className="text-sm text-zinc-500">нет данных</p>
          ) : (
            <div className="space-y-1.5">
              {aggregates.byDept.map((d) => (
                <BarRow
                  key={d.label}
                  label={d.label}
                  count={d.count}
                  max={aggregates.byDeptMax}
                  color="#0ea5e9"
                />
              ))}
            </div>
          )}
        </section>

        <section className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-5">
          <h2 className="text-sm font-semibold mb-3 text-zinc-900 dark:text-zinc-100">
            Топ авторов
          </h2>
          {aggregates.byAuthor.length === 0 ? (
            <p className="text-sm text-zinc-500">нет данных</p>
          ) : (
            <ol className="space-y-1 text-sm list-decimal list-inside">
              {aggregates.byAuthor.map((a) => (
                <li key={a.label} className="flex justify-between gap-2">
                  <span className="text-zinc-700 dark:text-zinc-300 truncate">
                    {a.label}
                  </span>
                  <span className="text-zinc-500 font-mono">{a.count}</span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      <section className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-5">
        <h2 className="text-sm font-semibold mb-3 text-zinc-900 dark:text-zinc-100">
          Динамика подачи (30 дней)
        </h2>
        <div className="flex items-end gap-0.5 h-24">
          {aggregates.dailyCounts.map((d) => {
            const h =
              aggregates.dailyMax > 0
                ? Math.max(2, (d.count / aggregates.dailyMax) * 100)
                : 2
            return (
              <div
                key={d.day}
                title={`${d.day}: ${d.count}`}
                className="flex-1 bg-amber-400 dark:bg-amber-500 rounded-sm hover:bg-amber-500 dark:hover:bg-amber-400 transition-colors"
                style={{ height: `${h}%` }}
              />
            )
          })}
        </div>
        <div className="mt-2 flex justify-between text-xs text-zinc-500">
          <span>{aggregates.dailyCounts[0]?.day ?? ''}</span>
          <span>всего за 30 дней: {aggregates.dailyTotal}</span>
          <span>
            {aggregates.dailyCounts[aggregates.dailyCounts.length - 1]?.day ?? ''}
          </span>
        </div>
      </section>
    </div>
  )
}

function BarRow({
  label,
  count,
  max,
  color,
}: {
  label: string
  count: number
  max: number
  color: string
}) {
  const pct = max > 0 ? (count / max) * 100 : 0
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="text-zinc-700 dark:text-zinc-300 leading-snug" title={label}>
          {label}
        </span>
        <span className="shrink-0 text-zinc-600 dark:text-zinc-400 text-xs font-mono tabular-nums">
          {count}
        </span>
      </div>
      <div className="h-2 bg-zinc-100 dark:bg-zinc-700 rounded overflow-hidden">
        <div
          className="h-full rounded transition-all"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  )
}

interface AggregateBucket {
  label: string
  count: number
}

interface Aggregates {
  byDept: AggregateBucket[]
  byDeptMax: number
  byAuthor: AggregateBucket[]
  dailyCounts: Array<{ day: string; count: number }>
  dailyMax: number
  dailyTotal: number
}

function computeAggregates(ideas: IdeaForAggregate[]): Aggregates {
  const deptCounts = new Map<string, number>()
  const authorCounts = new Map<string, number>()
  for (const i of ideas) {
    const dept = i.target_department_name?.trim()
    if (dept) deptCounts.set(dept, (deptCounts.get(dept) ?? 0) + 1)
    const author = i.author?.full_name?.trim()
    if (author) authorCounts.set(author, (authorCounts.get(author) ?? 0) + 1)
  }
  const byDept: AggregateBucket[] = Array.from(deptCounts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10)
  const byAuthor: AggregateBucket[] = Array.from(authorCounts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)

  // 30-day sparkline
  const dayKey = (d: Date) => d.toISOString().slice(0, 10)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const orderedKeys: string[] = []
  const counts = new Map<string, number>()
  for (let offset = 29; offset >= 0; offset--) {
    const d = new Date(today)
    d.setDate(d.getDate() - offset)
    const k = dayKey(d)
    orderedKeys.push(k)
    counts.set(k, 0)
  }
  for (const i of ideas) {
    const created = i.created_at?.slice(0, 10)
    if (created && counts.has(created)) {
      counts.set(created, (counts.get(created) ?? 0) + 1)
    }
  }
  const dailyCounts = orderedKeys.map((k) => ({
    day: k.slice(5),
    count: counts.get(k) ?? 0,
  }))
  const dailyMax = Math.max(0, ...dailyCounts.map((d) => d.count))
  const dailyTotal = dailyCounts.reduce((s, d) => s + d.count, 0)

  return {
    byDept,
    byDeptMax: Math.max(1, ...byDept.map((b) => b.count)),
    byAuthor,
    dailyCounts,
    dailyMax,
    dailyTotal,
  }
}
